import React from "react"
import { Link } from "gatsby"
import "../styles/about-section.scss"

interface StackGroup {
  label: string
  items: string[]
}

const STACK: StackGroup[] = [
  {
    label: "Languages",
    items: ["JavaScript", "TypeScript", "Python", "Go", "Bash", "SQL"],
  },
  {
    label: "Backend",
    items: [
      "Node.js",
      "NestJS",
      "Express",
      "Django",
      "Django REST Framework",
      "FastAPI",
      "gRPC",
    ],
  },
  {
    label: "Data & Streaming",
    items: [
      "PostgreSQL",
      "MongoDB",
      "Redis",
      "Apache Kafka",
      "RabbitMQ",
      "Hive",
      "Apache Spark",
    ],
  },
  {
    label: "Cloud & DevOps",
    items: [
      "AWS",
      "Azure",
      "Docker",
      "Kubernetes",
      "Terraform",
      "Jenkins",
      "GitHub Actions",
    ],
  },
  {
    label: "AI & Security",
    items: [
      "RAG pipelines",
      "Vector search",
      "LLM integration",
      "OAuth & JWT",
      "CSRF defense",
      "TLS hardening",
      "DevSecOps",
    ],
  },
]

interface FeaturedProject {
  slug: string
  title: string
  blurb: string
}

const FEATURED: FeaturedProject[] = [
  {
    slug: "/projects/nodejs-kafka-boilerplate",
    title: "Node.js + Apache Kafka Boilerplate",
    blurb:
      "Production-grade Kafka integration with Node.js — dynamic topics, auto-subscribing consumers, and Docker-ready microservice scaffolding.",
  },
  {
    slug: "/projects/hive-poco-streaming",
    title: "Hive + Spark Streaming Pipeline",
    blurb:
      "Realtime data pipeline that ingests events, lands them in Hive via Spark structured streaming, and exposes queryable analytics.",
  },
  {
    slug: "/projects/secure-session-based-jwt-authentication",
    title: "Session-bound JWT Authentication",
    blurb:
      "Hardened auth flow combining session storage with rotating JWTs — defends against token theft, replay, and CSRF.",
  },
  {
    slug: "/projects/csrf-protection-nodejs",
    title: "CSRF Protection for Node.js APIs",
    blurb:
      "Drop-in middleware suite implementing the OWASP CSRF cheat sheet — double-submit cookies, origin checks, SameSite enforcement.",
  },
  {
    slug: "/projects/grpc-nodejs",
    title: "gRPC Microservices in Node.js",
    blurb:
      "Schema-first gRPC services with proto contracts, streaming RPCs, and TLS — for low-latency internal traffic.",
  },
  {
    slug: "/projects/azure-aci-terraform-nodejs",
    title: "Azure ACI + Terraform + Node.js",
    blurb:
      "Infrastructure-as-code workflow deploying a containerized Node.js service to Azure Container Instances via Terraform.",
  },
  {
    slug: "/projects/video-transcoding",
    title: "Video Transcoding Service",
    blurb:
      "FFmpeg-driven transcoding worker that adapts video bitrates and formats for HLS / DASH delivery at scale.",
  },
  {
    slug: "/projects/nodejs-loadbalancing",
    title: "Node.js Load Balancing Patterns",
    blurb:
      "Reference implementations of round-robin, least-connections, and sticky-session load balancing in pure Node.js.",
  },
  {
    slug: "/projects/django-single-vendor-ecommerce",
    title: "Django Single-Vendor E-commerce",
    blurb:
      "End-to-end e-commerce stack on Django + DRF with cart, checkout, payment hooks, and admin tooling.",
  },
  {
    slug: "/projects/csrf-vulnerability-detection",
    title: "CSRF Vulnerability Detection",
    blurb:
      "Tooling that probes web applications for CSRF gaps — automated scanning, report generation, and remediation hints.",
  },
]

const AboutSection: React.FC = () => {
  return (
    <section className="about-section" aria-label="About Ayemun Hossain">
      <div className="about-section__intro">
        <h2 className="about-section__h2">
          About <em>Ayemun Hossain</em>
        </h2>
        <p className="about-section__lede">
          Ayemun Hossain is a Senior Software Engineer specialising in{" "}
          <strong>backend systems</strong>, <strong>cloud infrastructure</strong>,{" "}
          <strong>DevSecOps</strong>, and <strong>AI-native products</strong>.
          With 5+ years of production experience and 40+ shipped projects, he
          designs scalable distributed systems — from Node.js and Django APIs to
          Kafka-powered streaming pipelines, RAG retrieval stacks, and hardened
          cloud deployments on AWS and Azure.
        </p>
        <p className="about-section__lede">
          He works across the stack with a security-first mindset: writing
          high-throughput services, building CI/CD pipelines, integrating LLMs
          with vector search, and reviewing architectures for resilience and
          observability. The portfolio below catalogues open-source
          implementations, reference boilerplates, and study material covering
          the tools and patterns he uses day-to-day.
        </p>
      </div>

      <div className="about-section__stack">
        <h3 className="about-section__h3">Stack &amp; Specialisations</h3>
        <dl className="about-section__stack-grid">
          {STACK.map(group => (
            <div key={group.label} className="about-section__stack-row">
              <dt>{group.label}</dt>
              <dd>{group.items.join(" · ")}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="about-section__projects">
        <h3 className="about-section__h3">Featured Projects</h3>
        <ul className="about-section__project-list">
          {FEATURED.map(project => (
            <li key={project.slug} className="about-section__project">
              <Link
                to={project.slug}
                className="about-section__project-link"
              >
                <h4 className="about-section__project-title">{project.title}</h4>
              </Link>
              <p className="about-section__project-blurb">{project.blurb}</p>
            </li>
          ))}
        </ul>
        <p className="about-section__more">
          Explore the full archive of projects, write-ups, and study guides
          above — or reach out via <Link to="/contact">contact</Link>.
        </p>
      </div>
    </section>
  )
}

export default AboutSection
